/**
 * graphNav — single source of truth for navigating to the Security Graph.
 *
 * All pages that want to deep-link into the graph must use `navigateToGraph()`
 * instead of setting sessionStorage keys directly. The Security Graph then
 * fetches the rich multi-hop neighborhood from /api/v1/wiz-graph?issue_id=...
 * or /api/v1/wiz-graph?resource_id=... — Wiz-style. No pre-built client
 * chains; the graph engine owns attack-path rendering.
 */

export interface GraphNavOptions {
  /** ID of the issue to centre the graph on */
  issue_id?: string;
  /** Cloud resource ID to highlight */
  resource_id?: string;
  /** Human-readable resource name (used as search seed) */
  resource_name?: string;
  /** Free-text search term pre-filled in the graph search box */
  search_term?: string;
}

const NAV_KEY    = 'graph_navigate_to';
const FROM_ISSUE = 'graph_from_issue';

/**
 * Navigate to /graph, passing context via sessionStorage.
 * Cleans up stale keys before writing to avoid cross-contamination.
 */
export function navigateToGraph(opts: GraphNavOptions): void {
  sessionStorage.removeItem(NAV_KEY);
  sessionStorage.removeItem(FROM_ISSUE);
  // Legacy direct-path key — clear defensively in case older builds wrote it
  sessionStorage.removeItem('graph_attack_path_direct');

  const payload: Record<string, string> = {};
  if (opts.issue_id)      payload.issue_id      = opts.issue_id;
  if (opts.resource_id)   payload.resource_id   = opts.resource_id;
  if (opts.resource_name) payload.resource_name = opts.resource_name;
  if (opts.search_term)   payload.search_term   = opts.search_term;

  sessionStorage.setItem(NAV_KEY, JSON.stringify(payload));
  if (opts.issue_id || opts.resource_id) {
    sessionStorage.setItem(FROM_ISSUE, 'true');
  }

  window.location.href = '/graph';
}
